import { XMarkIcon, MapPinIcon, ClockIcon, ExclamationTriangleIcon, GlobeAltIcon } from '@heroicons/react/24/outline';

export default function AlertDetailsModal({ isOpen, onClose, alert }) {
  if (!isOpen || !alert) return null;

  const formatDate = (timestamp) => {
    return new Date(timestamp * 1000).toLocaleString();
  };

  const getSeverityColor = (priority) => {
    if (priority > 80) return 'text-red-400 bg-red-900/30 border-red-500';
    if (priority > 50) return 'text-orange-400 bg-orange-900/30 border-orange-500';
    return 'text-yellow-400 bg-yellow-900/30 border-yellow-500';
  };

  const getSeverityText = (priority) => {
    if (priority > 80) return 'High';
    if (priority > 50) return 'Medium';
    return 'Low';
  };

  const formatDuration = (begins, expires) => {
    const start = new Date(begins * 1000);
    const end = new Date(expires * 1000);
    const duration = Math.round((end - start) / (1000 * 60 * 60)); // hours
    return `${duration}h`;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 z-50 flex justify-end">
      <div className="w-full sm:w-[600px] bg-blueGray border border-borderGray h-full max-h-screen shadow-xl p-4 sm:p-6 overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-4 sm:mb-6">
          <h2 className="text-lg sm:text-xl font-semibold text-white">Alert Details</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors p-2 rounded-full hover:bg-gray-700"
          >
            <XMarkIcon className="w-5 h-5 sm:w-6 sm:h-6" />
          </button>
        </div>

        {/* Alert Information */}
        <div className="space-y-4 sm:space-y-6">
           {/* Alert Name and Severity */}
           <div className="bg-[#384355] p-3 sm:p-4 rounded-lg border border-borderGray">
            <div className="flex items-start sm:items-center justify-between mb-2 flex-col sm:flex-row gap-2 sm:gap-0">
              <h3 className="text-base sm:text-lg font-semibold text-white">{alert.details?.name}</h3>
              <span className={`px-3 py-1 rounded-full text-xs sm:text-sm font-medium border ${getSeverityColor(alert.details?.priority || 0)}`}>
                {getSeverityText(alert.details?.priority || 0)}
              </span>
            </div>
            <p className="text-gray-300 text-xs sm:text-sm">{alert.details?.bodyFull || alert.details?.body}</p>
          </div>

           {/* Location Information */}
           <div className="bg-[#384355] p-3 sm:p-4 rounded-lg border border-borderGray">
            <h4 className="text-sm sm:text-md font-semibold text-white mb-3 flex items-center">
              <MapPinIcon className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
              Location Information
            </h4>
            <div className="space-y-2 text-xs sm:text-sm">
              <div className="flex justify-between">
                <span className="text-gray-400">Location:</span>
                <span className="text-white text-right ml-2">{alert.place?.name || 'Unknown'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Country:</span>
                <span className="text-white text-right ml-2">{alert.place?.country?.toUpperCase() || '—'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Coordinates:</span>
                <span className="text-white font-mono text-xs">
                  {alert.loc?.lat?.toFixed(4)}, {alert.loc?.long?.toFixed(4)}
                </span>
              </div>
            </div>
          </div>

           {/* Timing Information */}
           <div className="bg-[#384355] p-4 rounded-lg border border-borderGray">
            <h4 className="text-md font-semibold text-white mb-3 flex items-center">
              <ClockIcon className="h-5 w-5 mr-2" />
              Timing Information
            </h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-400">Issued:</span>
                <span className="text-white">{formatDate(alert.timestamps?.issued)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Valid From:</span>
                <span className="text-white">{formatDate(alert.timestamps?.begins)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Valid Until:</span>
                <span className="text-white">{formatDate(alert.timestamps?.expires)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Duration:</span>
                <span className="text-white">{formatDuration(alert.timestamps?.begins, alert.timestamps?.expires)}</span>
              </div>
            </div>
          </div>

           {/* Alert Details */}
           <div className="bg-[#384355] p-4 rounded-lg border border-borderGray">
            <h4 className="text-md font-semibold text-white mb-3 flex items-center">
              <ExclamationTriangleIcon className="h-5 w-5 mr-2" />
              Alert Details
            </h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-400">Type:</span>
                <span className="text-white">{alert.details?.type || '—'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Category:</span>
                <span className="text-white capitalize">{alert.details?.cat || '—'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Priority:</span>
                <span className="text-white">{alert.details?.priority || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Emergency:</span>
                <span className={`${alert.details?.emergency ? 'text-red-400' : 'text-green-400'}`}>
                  {alert.details?.emergency ? 'Yes' : 'No'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Data Source:</span>
                <span className="text-white">{alert.dataSource || '—'}</span>
              </div>
            </div>
          </div>

          {/* Local Languages */}
          {/* {alert.localLanguages && alert.localLanguages.length > 0 && (
            <div className="bg-[#212c43] p-4 rounded-lg">
              <h4 className="text-md font-semibold text-white mb-3 flex items-center">
                <GlobeAltIcon className="h-5 w-5 mr-2" />
                Local Languages
              </h4>
              <div className="space-y-3">
                {alert.localLanguages.map((lang, idx) => (
                  <div key={idx} className="bg-gray-700 p-3 rounded">
                    <div className="text-sm text-gray-400 mb-1 font-medium">{lang.language.toUpperCase()}</div>
                    <div className="text-white font-medium mb-1">{lang.name}</div>
                    <div className="text-gray-300 text-sm">{lang.body}</div>
                  </div>
                ))}
              </div>
            </div>
          )} */}

          {/* Weather Zones */}
          {/* {alert.includes?.wxzones && alert.includes.wxzones.length > 0 && (
            <div className="bg-[#212c43] p-4 rounded-lg">
              <h4 className="text-md font-semibold text-white mb-3">Weather Zones</h4>
              <div className="flex flex-wrap gap-2">
                {alert.includes.wxzones.slice(0, 10).map((zone, idx) => (
                  <span key={idx} className="px-2 py-1 bg-primaryBlue text-white text-xs rounded">
                    {zone}
                  </span>
                ))}
                {alert.includes.wxzones.length > 10 && (
                  <span className="px-2 py-1 bg-gray-600 text-white text-xs rounded">
                    +{alert.includes.wxzones.length - 10} more
                  </span>
                )}
              </div>
            </div>
          )} */}

           {/* Status */}
           <div className="bg-[#384355] p-4 rounded-lg border border-borderGray">
            <h4 className="text-md font-semibold text-white mb-3">Status</h4>
            <div className="flex items-center space-x-2">
              <span className={`w-3 h-3 rounded-full ${alert.active ? 'bg-green-400' : 'bg-red-400'}`}></span>
              <span className="text-white">{alert.active ? 'Active' : 'Inactive'}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
